// components/industry-templates/CloudEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Cloud, Server, Zap, Shield, Code, Database } from 'lucide-react';

export const CloudEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Event Description */}
      <Card className="border-0 bg-gradient-to-br from-sky-500/20 to-blue-500/20 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-2xl">
            <Cloud className="w-8 h-8" style={{ color: theme.primary }} />
            Cloud Computing Summit
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-300 text-lg leading-relaxed mb-6">{structure.description}</p>
          
          {/* Show DevOps and Kubernetes indicators if available */}
          {(industryData?.devops || industryData?.kubernetes) && (
            <div className="grid grid-cols-2 gap-4">
              {industryData?.devops && (
                <div className="p-5 bg-white/10 rounded-xl text-center">
                  <div className="text-4xl mb-3">🔧</div>
                  <p className="text-xl font-bold text-white mb-2">DevOps</p>
                  <p className="text-slate-300 text-sm">CI/CD & Automation</p>
                </div>
              )}
              {industryData?.kubernetes && (
                <div className="p-5 bg-white/10 rounded-xl text-center">
                  <div className="text-4xl mb-3">☸️</div>
                  <p className="text-xl font-bold text-white mb-2">Kubernetes</p>
                  <p className="text-slate-300 text-sm">Container Orchestration</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Topics Covered */}
      {structure.topics && structure.topics.length > 0 && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Zap className="w-6 h-6" style={{ color: theme.primary }} />
              Cloud Topics & Technologies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {structure.topics.map((topic: string, idx: number) => (
                <Badge key={idx} style={{ backgroundColor: theme.primary }} className="text-lg py-3 px-6">
                  {topic}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cloud Service Providers */}
      <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(14, 165, 233, 0.1)' }}>
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Cloud className="w-6 h-6" style={{ color: theme.primary }} />
            Leading Cloud Platforms
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['AWS', 'Azure', 'Google Cloud', 'IBM Cloud'].map((provider, idx) => (
              <div key={idx} className="p-5 bg-white/10 rounded-xl text-center hover:scale-105 transition-transform border border-white/20">
                <div className="text-4xl mb-3">☁️</div>
                <h4 className="font-bold text-white">{provider}</h4>
                <Badge className="mt-2" style={{ backgroundColor: theme.primary }}>
                  Platform Demo
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Cloud Solutions & Services */}
      <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Server className="w-6 h-6" style={{ color: theme.primary }} />
            Cloud Solutions Showcase
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: '🚀', title: 'Cloud Migration', desc: 'Seamless migration strategies and best practices' },
              { icon: '🔐', title: 'Security & Compliance', desc: 'Enterprise-grade security solutions' },
              { icon: '📊', title: 'Data Analytics', desc: 'Big Data and AI/ML in the cloud' },
              { icon: '⚡', title: 'Serverless Computing', desc: 'Function-as-a-Service architectures' },
              { icon: '🌐', title: 'Multi-Cloud Strategy', desc: 'Hybrid and multi-cloud deployments' },
              { icon: '💰', title: 'Cost Optimization', desc: 'Managing cloud spending effectively' }
            ].map((solution, idx) => (
              <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4 hover:bg-white/10 transition-colors" style={{ borderColor: theme.primary }}>
                <div className="flex items-start gap-3">
                  <div className="text-3xl">{solution.icon}</div>
                  <div>
                    <h4 className="font-bold text-white mb-1">{solution.title}</h4>
                    <p className="text-sm text-slate-400">{solution.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Cloud Architecture Patterns */}
      <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Code className="w-6 h-6" style={{ color: theme.primary }} />
            Architecture & Design Patterns
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              'Microservices',
              'Event-Driven',
              'Serverless',
              'Service Mesh',
              'API Gateway',
              'CQRS Pattern',
              'Circuit Breaker',
              'Load Balancing',
              'Auto-Scaling'
            ].map((pattern, idx) => (
              <div key={idx} className="p-4 bg-white/5 rounded-lg text-center border border-white/10 hover:scale-105 transition-transform">
                <p className="text-white font-medium">{pattern}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Workshops & Hands-on Labs */}
      <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Database className="w-6 h-6" style={{ color: theme.primary }} />
            Hands-on Workshops
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              'Building Cloud-Native Applications',
              'Kubernetes Fundamentals & Advanced Topics',
              'Terraform & Infrastructure as Code',
              'CI/CD Pipeline with Cloud Services',
              'Monitoring & Observability in Cloud',
              'Cloud Security Best Practices'
            ].map((workshop, idx) => (
              <div key={idx} className="p-4 bg-white/5 rounded-lg flex items-center gap-3 hover:bg-white/10 transition-colors">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${theme.primary}30` }}>
                  <span className="text-xl" style={{ color: theme.primary }}>✓</span>
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">{workshop}</p>
                </div>
                <Badge className="bg-green-500 text-white">Hands-on Lab</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Security & Compliance */}
      <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-6 h-6" style={{ color: theme.primary }} />
            Cloud Security & Compliance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-white/5 rounded-lg p-5">
            <p className="text-white mb-4">Essential cloud security topics:</p>
            <div className="flex flex-wrap gap-2">
              {[
                'Zero Trust Architecture',
                'Identity & Access Management',
                'Data Encryption',
                'Compliance (SOC2, ISO)',
                'Security Monitoring',
                'Incident Response'
              ].map((topic, idx) => (
                <Badge key={idx} variant="outline" className="border-white/20 text-white text-sm py-2 px-3">
                  {topic}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Event Tags */}
      {structure.tags && structure.tags.length > 0 && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white">Event Tags</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {structure.tags.map((tag: string, idx: number) => (
                <Badge key={idx} variant="outline" className="border-white/20 text-white text-sm py-2 px-4">
                  #{tag}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Organizer Information */}
      <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white">Organized By</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-3xl" style={{ backgroundColor: theme.primary }}>
              ☁️
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-white text-xl mb-2">{structure.organizer_name}</h4>
              <div className="space-y-2 text-slate-300">
                {structure.organizer_email && (
                  <p className="flex items-center gap-2">
                    <span>📧</span> {structure.organizer_email}
                  </p>
                )}
                {structure.organizer_phone && (
                  <p className="flex items-center gap-2">
                    <span>📞</span> {structure.organizer_phone}
                  </p>
                )}
                {structure.organizer_website && (
                  <a href={structure.organizer_website} target="_blank" rel="noopener noreferrer" 
                     className="flex items-center gap-2 text-blue-400 hover:underline">
                    <span>🌐</span> {structure.organizer_website}
                  </a>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
